import { IconClose } from '@/styles/Icon'
import { Dialog, Listbox, Transition } from '@headlessui/react'
import { Fragment, useMemo, useRef, useState, type ChangeEvent } from 'react'
export interface FormSelectProps {
  value: string
  name: string
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  className?: string
  classSelectButtonName?: string
  children: JSX.Element[] | JSX.Element
  placeholder: string
  isError?: boolean | string
  isICon?: boolean
  selectContainClassName?: string
  zIndex?: string
  defaultTextColor?: string
  onDropdownOpen?: () => void
  placeholderColor?: string
  classNarrow?: string
  zIndexClassName?: string
  title: string
}

export function FormSelect({
  placeholder,
  title,
  placeholderColor,
  classNarrow,
  className,
  zIndexClassName = '',
  name,
  onChange,
  defaultTextColor = '',
  children,
  value,
  zIndex = 'absolute !z-50 outline-none focus:outline-none',
  isError = false,
  classSelectButtonName = '',
  selectContainClassName = '',
  isICon = true,
  onDropdownOpen,
}: FormSelectProps) {
  const [isSelected, setIsSelected] = useState(false)

  const onListBoxChange = (listBoxValue: string) => {
    onChange({
      target: { value: listBoxValue, name },
    } as ChangeEvent<HTMLInputElement>)

    setIsSelected(true)
  }

  const selectedValue = useMemo(() => {
    setIsSelected(!isSelected)
    return Array.isArray(children)
      ? children.find(f => f.props?.value === value)?.props?.children || null
      : children
  }, [value, children])
  const completeButtonRef = useRef(null)
  const optionChild = useMemo(
    () =>
      Array.isArray(children)
        ? children.filter(
            f =>
              !['FormTextRequire', 'FormTextHelper'].includes(
                f.type?.displayName
              )
          )
        : children,
    [children]
  )
  return (
    <div className={`relative ${className} ${zIndexClassName}`}>
      <Listbox value={value} onChange={onListBoxChange} name={name}>
        {({ open }) => (
          <>
            <Listbox.Button
              onClick={() => (onDropdownOpen ? onDropdownOpen() : null)}
              className={`flex w-full items-center justify-start text-base
              border ${
                isError ? 'border-states-error focus:ring-gray-light' : ''
              }
              focus:ring-gray-medium  rounded h-12 pl-3 ${classSelectButtonName}
               ${!isICon ? 'pr-3' : null} `}
            >
              <div
                className={`truncate ${
                  selectedValue === null ? defaultTextColor : ''
                }`}
              >
                {selectedValue === null || !selectedValue ? (
                  <span className={placeholderColor ? placeholderColor : ''}>
                    {placeholder}
                  </span>
                ) : (
                  selectedValue
                )}
              </div>
              {isICon && (
                <div className={`ml-auto px-[14px] ${classNarrow}`}>
                  {open ? (
                    <IconDown className='rotate-180' />
                  ) : (
                    <IconUp className='' />
                  )}
                </div>
              )}
            </Listbox.Button>
            <div>
              <Transition appear show={open} as={Fragment}>
                <Dialog
                  initialFocus={completeButtonRef}
                  as='div'
                  className={`relative z-[9999]`}
                  onClose={() => setIsSelected}
                >
                  <Transition.Child
                    as={Fragment}
                    enter='ease-in-out duration-500'
                    enterFrom='opacity-0'
                    enterTo='opacity-100'
                    leave='ease-in-out duration-500'
                    leaveFrom='opacity-100'
                    leaveTo='opacity-0'
                  >
                    <div className='fixed inset-0 left-0 bottom-0 bg-black-150 transition-opacity' />
                  </Transition.Child>
                  <div className='pointer-events-none z-50 w-full overflow-hidden fixed left-[50%] translate-x-[-50%] rounded-tr-sm rounded-tl-sm bottom-0 flex max-h-[55vh] h-full max-w-[440px] '>
                    <Transition.Child
                      as={Fragment}
                      enter='transform transition ease-in-out duration-500'
                      enterFrom='translate-y-[70%]'
                      enterTo='translate-y-[0%]'
                      leave='transform transition ease-in-out duration-500'
                      leaveFrom='translate-y-[0%]'
                      leaveTo='translate-y-[100%]'
                    >
                      <Dialog.Panel className='pointer-events-auto w-full max-w-[440px] h-full shadow-modal'>
                        <div
                          className={`bg-white pb-6 block transform overflow-hidden align-middle shadow-modal w-full`}
                        >
                          <div className='headerModal relative w-full h-[72px] px-3 pt-4 pb-3'>
                            <Dialog.Title
                              as='h2'
                              className={`text-titleSection`}
                            >
                              {title}
                            </Dialog.Title>
                            <button
                              onClick={onDropdownOpen}
                              className='absolute right-3 top-4 cursor-pointer z-10'
                            >
                              <IconClose />
                            </button>
                          </div>
                          <div
                            className={`max-h-[calc(55vh-96px)] min-h-[calc(55vh-96px)] max-w-[calc(100%-12px)] mr-3 pl-3 pr-2 scrollBarCS h-full`}
                          >
                            <Listbox.Options
                              className={`w-full relative bg-white-400 ${zIndex} ${selectContainClassName}`}
                              static
                            >
                              {optionChild}
                            </Listbox.Options>
                          </div>
                        </div>
                      </Dialog.Panel>
                    </Transition.Child>
                  </div>
                </Dialog>
              </Transition>
            </div>
          </>
        )}
      </Listbox>
    </div>
  )
}

export default FormSelect

interface IconUp {
  className?: string
}

function IconUp({ className = '' }: IconUp) {
  return (
    <svg
      className={className}
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M8.00041 11.0001C7.73521 11 7.4809 10.8946 7.29341 10.7071L3.29341 6.70708C3.11125 6.51848 3.01045 6.26588 3.01273 6.00368C3.01501 5.74148 3.12018 5.49067 3.30559 5.30526C3.491 5.11985 3.74181 5.01469 4.00401 5.01241C4.2662 5.01013 4.5188 5.11092 4.70741 5.29308L8.00041 8.58608L11.2934 5.29308C11.3857 5.19757 11.496 5.12139 11.618 5.06898C11.74 5.01657 11.8712 4.98898 12.004 4.98783C12.1368 4.98668 12.2685 5.01198 12.3914 5.06226C12.5143 5.11254 12.6259 5.18679 12.7198 5.28069C12.8137 5.37458 12.8879 5.48623 12.9382 5.60913C12.9885 5.73202 13.0138 5.8637 13.0127 5.99648C13.0115 6.12926 12.9839 6.26048 12.9315 6.38249C12.8791 6.50449 12.8029 6.61483 12.7074 6.70708L8.70741 10.7071C8.51991 10.8946 8.2656 11 8.00041 11.0001Z'
        fill='#303538'
      />
    </svg>
  )
}
interface IconDown {
  className?: string
  width?: number
  height?: number
}

function IconDown({ className = 'fill-black' }: IconDown) {
  return (
    <svg
      className={className}
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M8.00041 11.0001C7.73521 11 7.4809 10.8946 7.29341 10.7071L3.29341 6.70708C3.11125 6.51848 3.01045 6.26588 3.01273 6.00368C3.01501 5.74148 3.12018 5.49067 3.30559 5.30526C3.491 5.11985 3.74181 5.01469 4.00401 5.01241C4.2662 5.01013 4.5188 5.11092 4.70741 5.29308L8.00041 8.58608L11.2934 5.29308C11.3857 5.19757 11.496 5.12139 11.618 5.06898C11.74 5.01657 11.8712 4.98898 12.004 4.98783C12.1368 4.98668 12.2685 5.01198 12.3914 5.06226C12.5143 5.11254 12.6259 5.18679 12.7198 5.28069C12.8137 5.37458 12.8879 5.48623 12.9382 5.60913C12.9885 5.73202 13.0138 5.8637 13.0127 5.99648C13.0115 6.12926 12.9839 6.26048 12.9315 6.38249C12.8791 6.50449 12.8029 6.61483 12.7074 6.70708L8.70741 10.7071C8.51991 10.8946 8.2656 11 8.00041 11.0001Z'
        fill='#303538'
      />
    </svg>
  )
}
